<template>
    <div ref="selectDomRef">
        <a-tree-select class="custom-comp"
            style="position: relative"
            v-model:value="currentValue"
            :tree-data="treeOptions"
            :replace-fields="replaceFields"
            tree-checkable
            :labelInValue="true"
            allow-clear
            :disabled="disabled"
            :show-checked-strategy="SHOW_ALL"
            :treeCheckStrictly="true"
            show-search
            :placeholder="showPlaceHolder ? '请选择' + column.columnComment : ''"
            :treeNodeFilterProp="replaceFields.title"
            :dropdownStyle="{ maxHeight: '400px', overflow: 'auto' }"
            @change="changeTreeSelect"
        />
    </div>
</template>

<script lang="ts" name="filterTree" setup>
import { TreeSelect } from 'ant-design-vue';
import { reactive, ref, watch } from 'vue';
import { customQuery } from '../api';
import filterStore from '../store';
import { cloneDeep } from 'lodash-es';
import useExtraPlaceholder from '../hooks/useExtraPlaceholder';

const props = defineProps({
    rule: String,
    column: Object,
    value: [Array, String],
    disabled: Boolean,
});

const currentValue = ref([])
const treeOptions = ref([])
const SHOW_ALL = TreeSelect.SHOW_ALL;
let maxCount = null;

const replaceFields = reactive({
    children: 'children',
    title: 'title',
    key: 'key',
    value: 'value',
})

// 一二级 value 重复时改用 id 作为节点身份，提交前再还原成 value，见 shouldUseIdIdentity
let idMode = false;
// 接口数据自带的 disabled 需保留，不能被选项上限的置灰逻辑覆盖；但已选中项要放行，否则 tag 的 × 消失、取消不掉
const permanentlyDisabled = new Set();
let lastEmitted = null;
const keyToSubmit = new Map();
const submitToKey = new Map();
const submitToKeyLeaf = new Map();

watch(() => props.value, (value: Array<String>) => {
    if (idMode && lastEmitted === getSubmitSignature(value)) {
        return;
    }
    lastEmitted = null;
    currentValue.value = idMode ? mapSubmitToInternal(value) : value;
    whetherDisableOptions()
}, {
    immediate: true,
})

function walkTree(list, callback) {
    if (!list?.length) {
        return;
    }
    list.forEach(item => {
        walkTree(item.children, callback);
        callback(item);
    });
}

function shouldUseIdIdentity(list, valueKey) {
    const values = new Set();
    const ids = new Set();
    let duplicated = false;
    let idReady = true;
    walkTree(list, item => {
        const value = item[valueKey];
        duplicated = duplicated || values.has(value);
        values.add(value);
        if (!item.id || ids.has(item.id)) {
            idReady = false;
        }
        ids.add(item.id);
    });
    return duplicated && idReady;
}

function buildIdentityMap(list, valueKey, labelKey) {
    keyToSubmit.clear();
    submitToKey.clear();
    submitToKeyLeaf.clear();
    walkTree(list, item => {
        const value = item[valueKey];
        keyToSubmit.set(item.id, { label: item[labelKey], value });
        const compositeKey = getSubmitKey(value, item[labelKey]);
        if (!submitToKey.has(compositeKey)) {
            submitToKey.set(compositeKey, item.id);
        }
        if (!submitToKeyLeaf.has(value)) {
            submitToKeyLeaf.set(value, item.id);
        }
    });
}

function getSubmitKey(value, label) {
    return JSON.stringify([value, label]);
}

function getSubmitSignature(items) {
    if (!Array.isArray(items)) {
        return JSON.stringify(items ?? null);
    }
    return JSON.stringify(items.map(item => [item?.value, item?.label]));
}

function mapSubmitToInternal(value) {
    if (!Array.isArray(value)) {
        return value;
    }
    return value.map(item => {
        const key = submitToKey.get(getSubmitKey(item.value, item.label)) ?? submitToKeyLeaf.get(item.value);
        return key ? { label: item.label, value: key } : item;
    });
}

function toSubmitValue(items) {
    if (!idMode || !Array.isArray(items)) {
        return items;
    }
    return items.map(item => {
        const submitItem = keyToSubmit.get(item.value);
        return submitItem ? { ...submitItem } : item;
    });
}

const emit = defineEmits(['update:value', 'change'])

watch(() => props.column, () => {
    initOptions()
}, {
    immediate: true,
})

const selectDomRef = ref(null)
const { showPlaceHolder } = useExtraPlaceholder(selectDomRef, '.ant-select-selection-search-input')

function changeTreeSelect(selected, noData, { checked, triggerValue}) {
    const nodeData = findNode(treeOptions.value, triggerValue, null);
    recursion(nodeData, checked);
    whetherDisableOptions()
    const submitValue = toSubmitValue(currentValue.value);
    if (idMode) {
        lastEmitted = getSubmitSignature(submitValue);
    }
    emit('update:value', submitValue);
    emit('change', submitValue);
}

function whetherDisableOptions() {
    if (!maxCount) {
        return;
    }
    if (currentValue.value?.length >= maxCount) {
        recursionOptionDisable(true, treeOptions.value, currentValue.value)
    } else {
        recursionOptionDisable(false, treeOptions.value, currentValue.value)
    }
}

function recursion(node, isChecked) {
    if (!node?.children?.length) {
        return;
    }
    node.children.forEach(item => {
        if (isChecked) {
            if (!isInSelected(item) && (!maxCount || (maxCount && currentValue.value?.length < maxCount))) {
                currentValue.value?.push({
                    label: item[replaceFields.title],
                    value: item[replaceFields.value],
                });
            }
        } else {
            if (isInSelected(item)) {
                const index = currentValue.value?.findIndex(({value}) => item[replaceFields.value] === value);
                currentValue.value?.splice(index, 1);
            }
        }

        recursion(item, isChecked);
    })
}

function isInSelected(item) {
    return currentValue.value?.some(({value}) => value === item[replaceFields.value])
}

function findNode(options, value, result) {
    if (result || !options?.length) {
        return result;
    }
    options.forEach(item => {
        if (item[replaceFields.value] === value) {
            result = item;
            return;
        }
        result = findNode(item.children, value, result);
        
    });

    return result;
}

function recursionOptionDisable(disabled: boolean, treeOptions: Array<any>, selected: Array<string>) {
    if (!treeOptions?.length) {
        return;
    }
    treeOptions.forEach(item => {
        item.disabled = !isInSelected(item) && (disabled || permanentlyDisabled.has(item[replaceFields.value]));
        recursionOptionDisable(disabled, item.children, selected)
    })
}

async function initOptions() {
    maxCount = props.column.optionMaxNum;
    treeOptions.value = []
    idMode = false;
    permanentlyDisabled.clear();
    replaceFields.key = 'key';
    keyToSubmit.clear();
    submitToKey.clear();
    submitToKeyLeaf.clear();

    if (props.column.htmlType === 'area') {
        const res = await filterStore.getAreaData();
        replaceFields.title = 'areaName';
        replaceFields.value = 'areaCode';
        // 后台返回数据重复
        treeOptions.value = cloneDeep(res.slice(0, 34));
        return;
    }
    let options = [];
    const { interfaceUrl, interfaceMethod, returnParameterMapping, requestParameterMapping } = props.column
    const { labelKey, valueKey, } = (returnParameterMapping || {});
    if (interfaceUrl && interfaceMethod) {
        const result = await customQuery({
            url: interfaceUrl,
            method: interfaceMethod,
            params: requestParameterMapping,
        }) || [];
        replaceFields.title = labelKey ?? 'title';
        const submitValueKey = valueKey ?? 'value';
        idMode = shouldUseIdIdentity(result, submitValueKey);
        if (idMode) {
            buildIdentityMap(result, submitValueKey, replaceFields.title);
            replaceFields.value = 'id';
            replaceFields.key = 'id';
        } else {
            replaceFields.value = submitValueKey;
        }
        walkTree(result, item => {
            const key = item[replaceFields.value];
            if (item.disabled === true && key !== undefined && key !== null && key !== '') {
                permanentlyDisabled.add(key);
            }
        });
        options = result;
    } else {
        replaceFields.value = valueKey ?? 'value';
    }

    treeOptions.value = options;
    // 选项是异步加载的，首次挂载时 props.value 先于索引到达，需要重新回显一次
    lastEmitted = null;
    currentValue.value = idMode ? mapSubmitToInternal(props.value) : props.value;
    whetherDisableOptions();
}

</script>

<style lang="less" scoped>
:deep(.ant-select) {
    width: 100%;
}
:deep(.ant-select-selector) {
    max-height: 220px;
    overflow: auto;
}
:deep(.ant-select-selection-search) {
    width: 80%!important;
}

.custom-comp {
    width: 100%;
}
</style>
